export const PUBLIC_PROFILE_CACHE_TTL_MS = 5 * 60 * 1000;

function normalizePublicUsername(username) {
  return String(username || '').trim().toLowerCase().replace(/^@/, '').slice(0, 64);
}

export function getPublicProfileCacheKey(username) {
  const normalized = normalizePublicUsername(username);
  if (!normalized) return null;
  return `linkit_profile_${normalized}`;
}

export function readPublicProfileCache(username, ttlMs = PUBLIC_PROFILE_CACHE_TTL_MS) {
  if (typeof sessionStorage === 'undefined') return null;

  const cacheKey = getPublicProfileCacheKey(username);
  if (!cacheKey) return null;
  const clearCacheKey = () => {
    try {
      sessionStorage.removeItem(cacheKey);
    } catch {
      // Ignore storage access failures.
    }
  };

  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (!cached) return null;

    const parsed = JSON.parse(cached);
    const isFresh = Number.isFinite(parsed.cachedAt) && Date.now() - parsed.cachedAt < ttlMs;

    if (isFresh && parsed.profile && parsed.links) {
      return {
        profile: parsed.profile,
        links: parsed.links,
        version: parsed.version || null,
        profileUpdatedAt: parsed.profileUpdatedAt || null,
        linksUpdatedAt: parsed.linksUpdatedAt || null,
        linksCount: parsed.linksCount || parsed.links.length || 0,
      };
    }

    clearCacheKey();
  } catch {
    clearCacheKey();
  }

  return null;
}

export function writePublicProfileCache(username, profile, links, versionMeta = {}) {
  if (typeof sessionStorage === 'undefined') return;

  const cacheKey = getPublicProfileCacheKey(username);
  if (!cacheKey || !profile || !links) return;

  try {
    sessionStorage.setItem(cacheKey, JSON.stringify({
      profile,
      links,
      cachedAt: Date.now(),
      version: versionMeta.version || null,
      profileUpdatedAt: versionMeta.profileUpdatedAt || profile.updated_at || null,
      linksUpdatedAt: versionMeta.linksUpdatedAt || null,
      linksCount: versionMeta.linksCount ?? links.length,
    }));
  } catch {
    // Session storage is an optimization only.
  }
}

export function readInjectedPublicProfile(username) {
  if (typeof window === 'undefined') return null;

  const expectedUsername = normalizePublicUsername(username);
  const payload = window.__LINKIT_PRERENDERED_PROFILE__;
  const payloadUsername = normalizePublicUsername(payload?.username || payload?.profile?.username);

  if (!expectedUsername || !payloadUsername || expectedUsername !== payloadUsername) return null;
  if (!payload?.profile || !Array.isArray(payload.links)) return null;

  return {
    profile: payload.profile,
    links: payload.links,
    version: payload.version || null,
    profileUpdatedAt: payload.profileUpdatedAt || payload.profile?.updated_at || null,
    linksUpdatedAt: payload.linksUpdatedAt || null,
    linksCount: payload.linksCount ?? payload.links.length,
  };
}

export function clearPublicProfileCache(...usernames) {
  if (typeof sessionStorage === 'undefined') return;

  const cacheKeys = new Set(
    usernames
      .map(getPublicProfileCacheKey)
      .filter(Boolean)
  );

  for (const cacheKey of cacheKeys) {
    try {
      sessionStorage.removeItem(cacheKey);
    } catch {
      // Ignore storage access failures.
    }
  }
}
